import { CONFIG, setMode } from '../config/CONFIG.js';
import type { WeatherAPIInterface, WeatherModeInfo } from './types.js';

export const WeatherAPI: WeatherAPIInterface = {
  latitude: null,
  longitude: null,
  currentWeather: null,
  lastFetchTime: 0,
  fetchInterval: 10 * 60 * 1000,

  weatherCodeMap: {
    0: { mode: 'light', desc: 'Clear sky' },
    1: { mode: 'light', desc: 'Mainly clear' },
    2: { mode: 'light', desc: 'Partly cloudy' },
    3: { mode: 'light', desc: 'Overcast' },
    45: { mode: 'light', desc: 'Fog' },
    48: { mode: 'light', desc: 'Depositing rime fog' },
    51: { mode: 'light', desc: 'Light drizzle' },
    53: { mode: 'light', desc: 'Moderate drizzle' },
    55: { mode: 'heavy', desc: 'Dense drizzle' },
    56: { mode: 'light', desc: 'Light freezing drizzle' },
    57: { mode: 'heavy', desc: 'Dense freezing drizzle' },
    61: { mode: 'light', desc: 'Slight rain' },
    63: { mode: 'heavy', desc: 'Moderate rain' },
    65: { mode: 'heavy', desc: 'Heavy rain' },
    66: { mode: 'light', desc: 'Light freezing rain' },
    67: { mode: 'heavy', desc: 'Heavy freezing rain' },
    71: { mode: 'snow', desc: 'Slight snow' },
    73: { mode: 'snow', desc: 'Moderate snow' },
    75: { mode: 'snow', desc: 'Heavy snow' },
    77: { mode: 'snow', desc: 'Snow grains' },
    80: { mode: 'light', desc: 'Slight rain showers' },
    81: { mode: 'heavy', desc: 'Moderate rain showers' },
    82: { mode: 'heavy', desc: 'Violent rain showers' },
    85: { mode: 'snow', desc: 'Slight snow showers' },
    86: { mode: 'snow', desc: 'Heavy snow showers' },
    95: { mode: 'thunderstorm', desc: 'Thunderstorm' },
    96: { mode: 'thunderstorm', desc: 'Thunderstorm with slight hail' },
    99: { mode: 'thunderstorm', desc: 'Thunderstorm with heavy hail' },
  },

  async init() {
    try {
      const position = await this.getLocation();
      this.latitude = position.coords.latitude;
      this.longitude = position.coords.longitude;
      console.log(`Location: ${this.latitude}, ${this.longitude}`);
      await this.fetchWeather();
    } catch (error) {
      console.warn('Weather API init failed:', (error as Error).message);
      this.latitude = 39.9042;
      this.longitude = 116.4074;
      await this.fetchWeather();
    }
  },

  getLocation() {
    return new Promise<GeolocationPosition>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      });
    });
  },

  async fetchWeather() {
    const now = Date.now();
    if (now - this.lastFetchTime < this.fetchInterval && this.currentWeather) {
      return this.currentWeather;
    }

    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${this.latitude}&longitude=${this.longitude}&current_weather=true`;
      const response = await fetch(url);
      const data = await response.json();

      this.currentWeather = data.current_weather;
      this.lastFetchTime = now;

      console.log('Weather fetched:', this.currentWeather);
      return this.currentWeather;
    } catch (error) {
      console.error('Failed to fetch weather:', error);
      return null;
    }
  },

  getModeForWeather() {
    if (!this.currentWeather) return null;

    const weatherCode = this.currentWeather.weathercode;
    const mapping = this.weatherCodeMap[weatherCode];

    if (mapping) {
      return {
        mode: mapping.mode,
        description: mapping.desc,
        temperature: this.currentWeather.temperature,
        windspeed: this.currentWeather.windspeed,
      };
    }

    return {
      mode: 'auto',
      description: `Unknown (${weatherCode})`,
      temperature: this.currentWeather.temperature,
      windspeed: this.currentWeather.windspeed,
    };
  },

  async applyWeatherMode() {
    if (!CONFIG.weatherSyncEnabled) return;

    const weather = await this.fetchWeather();
    if (!weather) return;

    const modeInfo = this.getModeForWeather();
    if (modeInfo && modeInfo.mode !== 'auto') {
      setMode(modeInfo.mode as any);
      this.updateWeatherUI(modeInfo);
    }
  },

  updateWeatherUI(modeInfo: WeatherModeInfo) {
    const weatherInfo = document.getElementById('weather-info');
    if (weatherInfo) {
      weatherInfo.textContent = `${modeInfo.description} | ${modeInfo.temperature}°C`;
      weatherInfo.style.opacity = '1';
      setTimeout(() => {
        weatherInfo.style.opacity = '0.5';
      }, 3000);
    }
  },
};
